import { type JSX, useState } from 'react';
import { type Capacidades, type FaqDetalhe, ErroApi, api } from '../lib/api.js';
import {
  Alert,
  Button,
  Code,
  Fieldset,
  Group,
  List,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PreviaWhatsapp } from './PreviaWhatsapp.js';

interface Props {
  faq: FaqDetalhe;
  capacidades: Capacidades;
  categorias: string[];
  categoriaPendenteConteudo: string;
  aoSalvar: (atualizada: FaqDetalhe, pendentes: number) => void;
  aoFechar: () => void;
}

export function EditorFaq({
  faq,
  capacidades,
  categorias,
  categoriaPendenteConteudo,
  aoSalvar,
  aoFechar,
}: Props): JSX.Element {
  const [resposta, setResposta] = useState(faq.resposta);
  const [pergunta, setPergunta] = useState(faq.pergunta);
  const [categoria, setCategoria] = useState(faq.categoria);
  const [variacoes, setVariacoes] = useState(faq.variacoes.join('\n'));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const somenteLeitura = !capacidades.editar;

  const listaVariacoes = variacoes
    .split('\n')
    .map((v) => v.trim())
    .filter(Boolean);

  /**
   * O aviso de reindexação é derivado do MESMO critério do servidor: só
   * `pergunta` e `variacoes` exigem reembeddar, porque o embedding é do trecho,
   * não da resposta. Mostrar antes de salvar evita a surpresa de "editei um
   * typo e agora a base está pendente".
   */
  const mudouPergunta = pergunta.trim() !== faq.pergunta;
  const mudouVariacoes =
    listaVariacoes.length !== faq.variacoes.length ||
    listaVariacoes.some((v, i) => v !== faq.variacoes[i]);
  const exigiraReindexacao = mudouPergunta || mudouVariacoes;
  const mudouResposta = resposta !== faq.resposta;
  const mudouCategoria = categoria !== faq.categoria;
  const temMudanca = exigiraReindexacao || mudouResposta || mudouCategoria;

  /** FAQ sem id é CRIAÇÃO: o mesmo formulário serve aos dois caminhos. */
  const ehNova = faq.id === '';

  async function salvar(): Promise<void> {
    setSalvando(true);
    setErro(null);
    try {
      if (ehNova) {
        // Nasce `reindexacao_pendente`, como qualquer FAQ nova: o texto já
        // responde pelo menu, mas a busca livre só a encontra depois do
        // reindexar. Quem chamou avisa isso na tela.
        const criada = await api.post<{ faq: FaqDetalhe; pendentesDeReindexacao: number }>(
          '/admin/faqs',
          {
            pergunta: pergunta.trim(),
            resposta,
            categoria,
            variacoes: listaVariacoes,
          },
        );
        notifications.show({
          color: 'green',
          message: 'FAQ criada. Ela entra na busca livre depois da reindexação.',
        });
        aoSalvar(criada.faq, criada.pendentesDeReindexacao);
        return;
      }

      const resultado = await api.patch<{
        faq: FaqDetalhe;
        exigeReindexacao: boolean;
        pendentesDeReindexacao: number;
      }>(`/admin/faqs/${faq.id}`, {
        ...(mudouPergunta ? { pergunta: pergunta.trim() } : {}),
        ...(mudouResposta ? { resposta } : {}),
        ...(mudouCategoria ? { categoria } : {}),
        ...(mudouVariacoes ? { variacoes: listaVariacoes } : {}),
      });
      notifications.show({
        color: resultado.exigeReindexacao ? 'violet' : 'green',
        message: resultado.exigeReindexacao
          ? 'Salvo. A FAQ entrou na fila de reindexação.'
          : 'Salvo.',
      });
      aoSalvar(resultado.faq, resultado.pendentesDeReindexacao);
    } catch (e) {
      setErro(
        e instanceof ErroApi
          ? e.status === 409
            ? 'Já existe outra FAQ com essa pergunta. A pergunta é a chave usada pelo indexador e pelos atalhos de menu.'
            : e.message
          : 'falha ao salvar',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function alternarAtivacao(): Promise<void> {
    setSalvando(true);
    try {
      const atualizada = await api.post<FaqDetalhe>(`/admin/faqs/${faq.id}/ativacao`, {
        ativo: !faq.ativo,
      });
      notifications.show({
        color: 'green',
        message: atualizada.ativo ? 'FAQ reativada.' : 'FAQ desativada.',
      });
      aoSalvar(atualizada, -1);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'falha ao alterar ativação');
    } finally {
      setSalvando(false);
    }
  }

  /**
   * Fechar com alteração não salva pede confirmação.
   *
   * O texto de uma resposta de FAQ é trabalho de minutos, e `Esc` é perto de
   * tudo. A guarda vale para as três saídas — `Esc`, o X do modal e o botão —
   * porque uma saída sem guarda anula as outras duas.
   */
  const sujo = ehNova ? Boolean(pergunta.trim() || resposta.trim()) : temMudanca;
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);

  function tentarFechar(): void {
    if (sujo && !salvando) setConfirmandoSaida(true);
    else aoFechar();
  }

  return (
    /**
     * MODAL, não bloco no fim da página.
     *
     * Antes este formulário era revelado DEPOIS da tabela: com 53 FAQs, quem
     * clicava em "Editar" na primeira linha não via nada acontecer e precisava
     * rolar a página inteira para achar o formulário. Editar FAQ é tarefa
     * focada — abrir, preencher, salvar, fechar — e enquanto ela acontece a
     * lista atrás não importa.
     */
    <Modal
      opened
      onClose={tentarFechar}
      title={ehNova ? 'Nova FAQ' : 'Editar FAQ'}
      size="xl"
      /* Tela cheia no celular: são seis campos e uma prévia. */
      fullScreen={typeof window !== 'undefined' && window.innerWidth < 768}
    >
      <Stack gap="sm">

        {categoria === categoriaPendenteConteudo && (
          <Alert color="orange" variant="light" title={`Categoria “${categoriaPendenteConteudo}”`}>
            Esta FAQ existe para encaminhar à secretaria, não para responder. Ao preencher com
            conteúdo real, <strong>troque a categoria</strong> — senão o bot vai continuar
            transbordando depois de enviar a resposta.
          </Alert>
        )}

        {faq.reindexacaoPendente && (
          <Alert color="violet" variant="light" title="Reindexação pendente">
            A pergunta ou as variações mudaram. A busca ainda usa os embeddings <em>antigos</em> —
            nada quebrou, mas o texto novo só passa a ser encontrado depois da reindexação.
            {faq.trechosSemEmbedding.length > 0 && (
              <List size="sm" mt="xs">
                {faq.trechosSemEmbedding.map((t) => (
                  <List.Item key={t}>
                    <Code>{t}</Code> — ainda sem embedding
                  </List.Item>
                ))}
              </List>
            )}
          </Alert>
        )}

        <Select
          label="Categoria"
          w={260}
          allowDeselect={false}
          disabled={somenteLeitura}
          value={categoria}
          onChange={(v) => setCategoria(v ?? categoria)}
          data={[...new Set([categoria, ...categorias])].map((c) => ({ value: c, label: c }))}
        />

        <Textarea
          label="Resposta"
          description="efeito imediato, não exige reindexação"
          autosize
          minRows={10}
          maxRows={20}
          disabled={somenteLeitura}
          value={resposta}
          onChange={(e) => setResposta(e.currentTarget.value)}
        />

        <PreviaWhatsapp texto={resposta} />

        <Fieldset
          legend="Pergunta e variações — mudar aqui exige reindexação (gasta API)"
          variant={exigiraReindexacao ? 'filled' : 'default'}
        >
          <Stack gap="sm">
            <TextInput
              label="Pergunta canônica"
              /* Foco ao abrir: é o primeiro campo da tarefa. */
              data-autofocus
              disabled={somenteLeitura}
              value={pergunta}
              onChange={(e) => setPergunta(e.currentTarget.value)}
            />
            <Textarea
              label="Variações"
              description={`uma por linha — ${listaVariacoes.length} agora`}
              autosize
              minRows={6}
              maxRows={16}
              disabled={somenteLeitura}
              value={variacoes}
              onChange={(e) => setVariacoes(e.currentTarget.value)}
            />
            {exigiraReindexacao && (
              <Alert color="orange" variant="light">
                Ao salvar, esta FAQ entra na fila de reindexação. Até um admin reindexar, a busca
                continua usando os embeddings antigos.
              </Alert>
            )}
          </Stack>
        </Fieldset>

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Group gap="sm" wrap="wrap">
          <Button
            variant="filled"
            loading={salvando}
            disabled={
              somenteLeitura ||
              (ehNova ? !pergunta.trim() || !resposta.trim() : !temMudanca)
            }
            onClick={salvar}
          >
            {ehNova ? 'Criar FAQ' : 'Salvar'}
          </Button>
          {/* Ativação e contagem de trechos só existem para FAQ que já existe. */}
          {!ehNova && (
            <>
              <Button loading={salvando} disabled={somenteLeitura} onClick={alternarAtivacao}>
                {faq.ativo ? 'Desativar' : 'Reativar'}
              </Button>
              <Text size="xs" c="dimmed">
                {faq.trechosIndexados} trecho(s) indexado(s). Desativar não apaga: o histórico de
                atendimentos aponta para esta FAQ.
              </Text>
            </>
          )}
          {ehNova && (
            <Text size="xs" c="dimmed">
              A FAQ nasce aguardando reindexação: o texto já responde por um item de menu, mas a
              busca livre só a encontra depois que um admin reindexar.
            </Text>
          )}
          <Button variant="subtle" onClick={tentarFechar}>
            Fechar
          </Button>
        </Group>
      </Stack>

      {/* Confirmação de saída: empilhada sobre o formulário, não no lugar
          dele — quem desistir de sair volta ao texto onde estava. */}
      <Modal
        opened={confirmandoSaida}
        onClose={() => setConfirmandoSaida(false)}
        title="Descartar o que você escreveu?"
        centered
        zIndex={400}
      >
        <Stack gap="md">
          <Text size="sm">
            {ehNova
              ? 'A FAQ não foi criada. O que está escrito aqui é perdido.'
              : 'As alterações desta FAQ não foram salvas e serão perdidas.'}
          </Text>
          <Group justify="flex-end">
            <Button onClick={() => setConfirmandoSaida(false)}>Continuar editando</Button>
            <Button color="red" variant="filled" onClick={aoFechar}>
              Descartar e fechar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}
